'use client';

import { useInfiniteQuery, useQuery } from '@tanstack/react-query';

import { isAdminRole } from '@/services/firebaseServices/AuthServices/helpers';
import { getSpaceCodeService, getSpaceSettingsService, getSpacesListService } from '@/services/httpServices/SpacesServices';
import type { GetSpaceCodeServiceConfig, GetSpaceSettingsServiceConfig, SpaceCodeResponse, SpaceSettingsResponse, SpacesListResponse, SpacesListServiceConfig } from '@/services/httpServices/SpacesServices/types';

import { buildStandardPaginatedParams } from './createInfiniteQuery';
import { infiniteSpacesListKeys, spaceCodeKeys, spaceSettingsKeys, spacesListKeys } from './spaceQueryKeys';
import { useQueryConfig } from './useQueryConfig';
import { useUserRoleQuery } from './useUserQuery';

/**
 * Hook for fetching spaces list from API
 * IMPORTANT: This is an admin-only endpoint. The hook automatically checks
 * if the user is an admin and only fetches if they are.
 * Non-admin users should use spaces from their JWT claims instead.
 */
export function useSpacesListQuery(
  params?: SpacesListServiceConfig,
  enabled?: boolean,
) {
  const { isEnabled, httpRequestConfig } = useQueryConfig();

  // Get user role to check if they're admin
  // Use params httpRequestConfig if available, otherwise use context config
  const configForRoleCheck = params?.httpRequestConfig ?? httpRequestConfig;
  const { data: userPermissions } = useUserRoleQuery({
    httpRequestConfig: configForRoleCheck,
  });

  // Only admins can fetch all spaces from the API
  const isAdmin = isAdminRole(userPermissions?.role);

  return useQuery<SpacesListResponse>({
    queryKey: spacesListKeys(params),
    queryFn: async () => {
      if (params === undefined) {
        throw new Error('Spaces list params are required');
      }
      return getSpacesListService(params);
    },
    // Only enable if: has space context, is admin, and either explicitly enabled or has params
    enabled: isEnabled && isAdmin && (enabled ?? params !== undefined),
  });
}

/**
 * Hook for fetching spaces list with infinite scrolling
 * IMPORTANT: This is an admin-only endpoint
 */
export function useInfiniteSpacesQuery(
  initialParams: SpacesListServiceConfig,
  enabled: boolean = true,
) {
  const { isEnabled, httpRequestConfig } = useQueryConfig();

  const configForRoleCheck = initialParams?.httpRequestConfig ?? httpRequestConfig;
  const { data: userPermissions } = useUserRoleQuery({
    httpRequestConfig: configForRoleCheck,
  });

  const isAdmin = isAdminRole(userPermissions?.role);

  return useInfiniteQuery<SpacesListResponse>({
    queryKey: infiniteSpacesListKeys(initialParams),
    queryFn: async ({ pageParam }) => {
      const paginationParams = buildStandardPaginatedParams(
        initialParams,
        pageParam ? String(pageParam) : undefined,
      );
      return getSpacesListService(paginationParams);
    },
    initialPageParam: '',
    getNextPageParam: (lastPage) => {
      if (lastPage.pageInfo.hasNextPage === true && lastPage.pageInfo.endCursor !== null) {
        return lastPage.pageInfo.endCursor;
      }
      return undefined;
    },
    getPreviousPageParam: (firstPage) => {
      if (firstPage.pageInfo.hasPreviousPage === true && firstPage.pageInfo.startCursor !== null) {
        return firstPage.pageInfo.startCursor;
      }
      return undefined;
    },
    enabled: isEnabled && isAdmin && enabled,
  });
}

export function useSpaceSettingsQuery(
  config?: GetSpaceSettingsServiceConfig,
  enabled?: boolean,
) {
  const { isEnabled } = useQueryConfig();

  return useQuery<SpaceSettingsResponse>({
    queryKey: spaceSettingsKeys(config),
    queryFn: async () => {
      if (config === undefined) {
        throw new Error('Space settings config is required');
      }
      return getSpaceSettingsService(config);
    },
    enabled: isEnabled && (enabled ?? (config !== undefined && config.params?.path.spaceId !== undefined && config.params?.path.spaceId !== '')),
  });
}

export function useSpaceCodeQuery(
  config?: GetSpaceCodeServiceConfig,
  enabled?: boolean,
) {
  const { isEnabled } = useQueryConfig();

  return useQuery<SpaceCodeResponse>({
    queryKey: spaceCodeKeys(config),
    queryFn: async () => {
      if (config === undefined) {
        throw new Error('Space code config is required');
      }
      return getSpaceCodeService(config);
    },
    enabled: isEnabled && (enabled ?? (config !== undefined && config.params?.spaceId !== undefined && config.params?.spaceId !== '')),
  });
}
